/**
 * Delete Team Confirmation Modal
 * Enhanced confirmation dialog that requires typing the team name
 * Shows affected user count and has stricter validation
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './Button';

/**
 * DeleteTeamConfirmModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler (called on cancel)
 * @param {Function} props.onConfirm - Confirm handler
 * @param {string} props.teamName - Name of team to delete
 * @param {number} props.userCount - Number of users in the team
 * @param {boolean} props.loading - Whether deletion is in progress
 */
const DeleteTeamConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    teamName = '',
    userCount = 0,
    loading = false
}) => {
    const { t } = useTranslation();
    const [confirmText, setConfirmText] = useState('');
    const [isValid, setIsValid] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setConfirmText('');
            setIsValid(false);
        }
    }, [isOpen]);

    // Validate that the typed text matches the team name
    useEffect(() => {
        setIsValid(confirmText.trim() === teamName.trim());
    }, [confirmText, teamName]);

    const handleConfirm = () => {
        if (!loading && isValid) {
            onConfirm();
        }
    };

    const handleCancel = () => {
        if (!loading) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            title={t('teamManagement.deleteTeamTitle')}
            maxWidth="500px"
            zIndex={1200}
            footer={
                <>
                    <Button
                        onClick={handleCancel}
                        variant="secondary"
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="danger"
                        loading={loading}
                        disabled={!isValid || loading}
                    >
                        {loading ? t('teamManagement.deleting') : t('teamManagement.confirmDelete')}
                    </Button>
                </>
            }
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0.5rem 0'
            }}>
                {/* Warning Icon */}
                <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#e74c3c20',
                    border: '2px solid #e74c3c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#e74c3c',
                    alignSelf: 'center'
                }}>
                    !
                </div>

                {/* Warning Message */}
                <p style={{
                    fontSize: '1rem',
                    color: 'var(--fg-primary)',
                    margin: '0 0 1rem 0',
                    lineHeight: '1.5',
                    textAlign: 'center'
                }}>
                    {t('teamManagement.deleteWarning', { team: teamName })}
                </p>

                {/* User Count Warning */}
                {userCount > 0 && (
                    <div style={{
                        padding: '1rem',
                        backgroundColor: '#e74c3c20',
                        border: '1px solid #e74c3c',
                        borderRadius: '6px',
                        marginBottom: '1rem'
                    }}>
                        <p style={{
                            fontSize: '0.9rem',
                            color: 'var(--fg-primary)',
                            margin: 0,
                            textAlign: 'center'
                        }}>
                            <strong>{t('teamManagement.userCountWarning', { count: userCount })}</strong>
                        </p>
                    </div>
                )}

                {/* Details */}
                <p style={{
                    fontSize: '0.875rem',
                    color: '#a0aec0',
                    margin: '0 0 1.5rem 0',
                    lineHeight: '1.4',
                    textAlign: 'center'
                }}>
                    {t('teamManagement.deleteDetails')}
                </p>

                {/* Confirmation Input */}
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontSize: '0.9rem',
                        color: 'var(--fg-primary)',
                        fontWeight: '600'
                    }}>
                        {t('teamManagement.confirmDeletePrompt')}
                    </label>
                    <p style={{
                        fontSize: '0.85rem',
                        color: 'var(--muted)',
                        margin: '0 0 0.5rem 0'
                    }}>
                        {t('teamManagement.typeToConfirm')}: <strong style={{ color: '#e74c3c' }}>{teamName}</strong>
                    </p>
                    <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={teamName}
                        disabled={loading}
                        autoFocus
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: `2px solid ${isValid ? '#27ae60' : 'var(--surface)'}`,
                            borderRadius: '6px',
                            backgroundColor: 'var(--surface)',
                            color: 'var(--fg-primary)',
                            fontSize: '1rem',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                        }}
                    />
                </div>

                {/* Validation feedback */}
                {confirmText && !isValid && (
                    <p style={{
                        fontSize: '0.8rem',
                        color: '#e74c3c',
                        margin: '0.5rem 0 0 0',
                        textAlign: 'center'
                    }}>
                        {t('teamManagement.nameDoesNotMatch')}
                    </p>
                )}

                {confirmText && isValid && (
                    <p style={{
                        fontSize: '0.8rem',
                        color: '#27ae60',
                        margin: '0.5rem 0 0 0',
                        textAlign: 'center'
                    }}>
                        ✓ {t('teamManagement.confirmationValid')}
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default DeleteTeamConfirmModal;

